import {
  GASTRONOMIE_PASS_TEMPLATE,
  type GastronomiePassTemplate,
} from './marketingPass';
import {
  PACKAGE_TIER_KEYS,
  PLATFORM_VERTICAL_KEY_VALUES,
  PLATFORM_VERTICAL_KEYS,
  THEME_ID,
  type PackageTierKey,
  type PlatformVerticalKey,
  type ThemeId,
} from '../catalog/platformCatalogIds';
import { PLATFORM_VERTICAL_MATRIX } from '../matrix/platformVerticalMatrix';
import { resolveDefaultColorSetId, resolveThemeSpacingEnum } from '../theme/themeResolve';

/** Stable preview key: `{vertical}/{themeId}` */
export type MarketplaceTemplateVariantId = `${PlatformVerticalKey}/${ThemeId}`;

export type MarketplaceTemplatePreviewViewport = 'desktop' | 'mobile';

/** Gallery tile key: `{vertical}/{themeId}/{viewport}` */
export type MarketplaceTemplatePreviewId =
  `${MarketplaceTemplateVariantId}/${MarketplaceTemplatePreviewViewport}`;

export const MARKETPLACE_TEMPLATE_PREVIEW_VIEWPORTS = [
  'mobile',
  'desktop',
] as const satisfies readonly MarketplaceTemplatePreviewViewport[];

export interface MarketplaceTemplateVariant {
  id: MarketplaceTemplateVariantId;
  vertical: PlatformVerticalKey;
  themeId: ThemeId;
  themeName: string;
  colorsetId: string;
  passTemplate: GastronomiePassTemplate;
  previewFile: string;
}

export interface MarketplaceTemplatePreviewTile {
  id: MarketplaceTemplatePreviewId;
  variant: MarketplaceTemplateVariant;
  viewport: MarketplaceTemplatePreviewViewport;
  previewFile: string;
}

const VERTICAL_PASS_TEMPLATE: Record<PlatformVerticalKey, GastronomiePassTemplate> = {
  [PLATFORM_VERTICAL_KEYS.restaurant]: GASTRONOMIE_PASS_TEMPLATE.fineDining,
  [PLATFORM_VERTICAL_KEYS.cafe]: GASTRONOMIE_PASS_TEMPLATE.fastCasual,
  [PLATFORM_VERTICAL_KEYS.bar]: GASTRONOMIE_PASS_TEMPLATE.barBistro,
  [PLATFORM_VERTICAL_KEYS.foodTruck]: GASTRONOMIE_PASS_TEMPLATE.foodTruck,
};

export function marketplaceTemplateVariantId(
  vertical: PlatformVerticalKey,
  themeId: ThemeId,
): MarketplaceTemplateVariantId {
  return `${vertical}/${themeId}`;
}

export function marketplaceTemplatePreviewId(
  variantId: MarketplaceTemplateVariantId,
  viewport: MarketplaceTemplatePreviewViewport,
): MarketplaceTemplatePreviewId {
  return `${variantId}/${viewport}`;
}

export function getTemplatePreviewPublicPath(
  id: MarketplaceTemplateVariantId,
  viewport: MarketplaceTemplatePreviewViewport = 'desktop',
): string {
  // Bust browser cache when preview WebPs are regenerated.
  return `/previews/${id}/${viewport}.webp?v=${MARKETPLACE_PREVIEW_ASSET_REVISION}`;
}

/** Bump when regenerating marketplace / onboarding preview WebPs. */
export const MARKETPLACE_PREVIEW_ASSET_REVISION = '2026-10-26-colorset-oklch';

/**
 * Themes always captured as Plus (Premium homepage — e.g. tasting / Omakase).
 * Others interleave recommended ↔ plus by theme order within the vertical.
 */
const MARKETPLACE_CAPTURE_PLUS_THEMES = new Set<ThemeId>([THEME_ID.hinokiCounter]);

/**
 * Families that cannot seed Plus-only homepage blocks (imbiss / bistro exclude
 * tasting & private party). Capturing them as Plus only shrinks or clones Growth.
 */
const MARKETPLACE_CAPTURE_RECOMMENDED_THEMES = new Set<ThemeId>([
  THEME_ID.mustardRail,
  THEME_ID.tahiniWindow,
  THEME_ID.boldStreet,
  THEME_ID.bistroBrasserie,
]);

/**
 * Package tier for marketplace / onboarding gallery WebPs.
 * Interleaves Growth Engine (recommended) and Premium (plus) by theme order
 * within each vertical so the gallery shows both compositions.
 */
export function resolveMarketplaceCaptureTier(
  vertical: PlatformVerticalKey,
  themeId: ThemeId,
): PackageTierKey {
  if (MARKETPLACE_CAPTURE_RECOMMENDED_THEMES.has(themeId)) {
    return PACKAGE_TIER_KEYS.recommended;
  }
  if (MARKETPLACE_CAPTURE_PLUS_THEMES.has(themeId)) {
    return PACKAGE_TIER_KEYS.plus;
  }
  const themes = PLATFORM_VERTICAL_MATRIX[vertical]?.themes ?? [];
  const index = themes.findIndex((theme) => theme.id === themeId);
  if (index < 0) {
    return PACKAGE_TIER_KEYS.recommended;
  }
  return index % 2 === 1 ? PACKAGE_TIER_KEYS.plus : PACKAGE_TIER_KEYS.recommended;
}

/** All vertical × theme combinations from PLATFORM_VERTICAL_MATRIX. */
export function listMarketplaceTemplateVariants(): MarketplaceTemplateVariant[] {
  const variants: MarketplaceTemplateVariant[] = [];

  for (const vertical of PLATFORM_VERTICAL_KEY_VALUES) {
    const definition = PLATFORM_VERTICAL_MATRIX[vertical];
    for (const theme of definition.themes) {
      const colorsetId = resolveDefaultColorSetId(theme.id);
      if (!colorsetId) continue;

      const id = marketplaceTemplateVariantId(vertical, theme.id);
      variants.push({
        id,
        vertical,
        themeId: theme.id,
        themeName: theme.name,
        colorsetId,
        passTemplate: VERTICAL_PASS_TEMPLATE[vertical],
        previewFile: `${id}/desktop.webp`,
      });
    }
  }

  return variants;
}

/** Masonry gallery tiles — one mobile + one desktop capture per template variant. */
export function listMarketplaceTemplatePreviewTiles(): MarketplaceTemplatePreviewTile[] {
  const tiles: MarketplaceTemplatePreviewTile[] = [];

  for (const variant of listMarketplaceTemplateVariants()) {
    for (const viewport of MARKETPLACE_TEMPLATE_PREVIEW_VIEWPORTS) {
      tiles.push({
        id: marketplaceTemplatePreviewId(variant.id, viewport),
        variant,
        viewport,
        previewFile: `${variant.id}/${viewport}.webp`,
      });
    }
  }

  return tiles;
}

export interface DesignLabCaptureQuery {
  locale?: string;
  /** Overrides theme default spacing when set. */
  spacing?: string;
  viewport?: 'desktop' | 'mobile' | 'tablet';
  /** Package tier for bootstrap content (marketplace defaults to recommended). */
  tier?: string;
}

/**
 * Design Lab URL for Playwright preview capture (`capture=1` ingress).
 * Blocks are injected via `window.__WS_BOOTSTRAP_CAPTURE__` from studio bootstrap builders —
 * not Design Lab page fixtures.
 */
export function buildDesignLabCapturePath(
  variant: MarketplaceTemplateVariant,
  query: DesignLabCaptureQuery = {},
): string {
  const locale = query.locale ?? 'de';
  const spacing = query.spacing ?? resolveThemeSpacingEnum(variant.themeId);

  const params = new URLSearchParams({
    locale,
    spacing,
    capture: '1',
    theme: variant.themeId,
    colorset: variant.colorsetId,
    vertical: variant.vertical,
  });

  if (query.tier) {
    params.set('tier', query.tier);
  }

  const viewport = query.viewport ?? 'desktop';
  if (viewport !== 'desktop') {
    params.set('viewport', viewport);
  }

  return `/matrix/bootstrap-capture?${params.toString()}`;
}
